import React, { useEffect, useMemo } from 'react';
import { SdfFocusPane, SdfPromoBox } from '@waypoint/react-components';
import { PromoBoxContainer } from './styles';
import { sendGA4Data, HeaderSmartSearchClose } from '../../analytics/events';
import { CONFIG } from '../../utils/config';
import { loadRemoteModule } from '../../utils/loadRemoteModule';

let SmartSearchModule = null;
let mountedInstance = null;
let isCurrentlyMounted = false;

const SMART_SEARCH_ROOT_ID = 'smart-search-root';
function getSmartSearchContainer() {
  return document.querySelector(`#${SMART_SEARCH_ROOT_ID}`);
}

function cleanupContainer() {
  const container = getSmartSearchContainer();
  if (container) container.innerHTML = '';
}

async function loadSmartSearchModule() {
  if (SmartSearchModule) return;
  try {
    // Fetch the Smart Search remote only when the pane is opened.
    const module = await loadRemoteModule({
      scope: 'embd_smart_search_ui',
      remoteBaseUrl: CONFIG.SMARTSEARCH_MFE_URL,
      exposedModule: './SmartSearchApp',
    });
    SmartSearchModule = module?.default ?? module?.SmartSearchApp ?? module;
  } catch (error) {
    console.error('❌ Smart Search module loading failed:', error);
    throw error;
  }
}

export async function bootstrap() {
  return Promise.resolve();
}

export async function mount(options, props, onContactUsClick, onProductClick, onSolutionsClick) {
  await loadSmartSearchModule();

  const smartSearchContainer = getSmartSearchContainer();
  if (!smartSearchContainer) {
    console.error(`❌ Smart Search container (#${SMART_SEARCH_ROOT_ID}) not found in DOM`);
    throw new Error('Smart Search container not found');
  }

  cleanupContainer();

  const mountOptions = {
    ...options,
    ...(props?.customProps || {})
  };

    // Ensure navigation is always available to the remote MFE.
  // Some MFEs only read navigation from `options` rather than single-spa props.
  mountOptions.navigate = props?.navigate || mountOptions.navigate || window.singleSpa?.navigateToUrl;
  mountOptions.navigateToUrl = props?.navigate || mountOptions.navigateToUrl || window.singleSpa?.navigateToUrl;

  try {
    if (typeof SmartSearchModule?.mount !== 'function') {
      console.error('❌ No mount function found in Smart Search module');
      console.log('Available module exports:', Object.keys(SmartSearchModule));
      throw new Error('Smart Search mount function not found');
    }

    mountedInstance = await SmartSearchModule.mount(
      smartSearchContainer,
      {
        options: mountOptions,
        onContactUsClick,
        onProductClick,
        onSolutionsClick
      },
      props
    );
    console.log('instance', mountedInstance);
    isCurrentlyMounted = true;
    console.log('✅ SmartSearch mounted successfully');
  } catch (error) {
    console.error('❌ SmartSearch mount error:', error);
    isCurrentlyMounted = false;
    mountedInstance = null;
    cleanupContainer();
    throw error;
  }
}

export async function unmount(props) {
  await loadSmartSearchModule();
  console.log('SmartSearch unmount called');

  if (!isCurrentlyMounted) {
    console.log('SmartSearch not currently mounted, skipping unmount');
    return;
  }

  try {
    if (typeof SmartSearchModule?.unmount === 'function') {
      await SmartSearchModule.unmount(props);
      console.log('✅ SmartSearch module unmount completed');
    }
  } catch (error) {
    console.error('⚠️ SmartSearch unmount error:', error);
  } finally {
    cleanupContainer();
    mountedInstance = null;
    isCurrentlyMounted = false;
  }
}


export default function SmartSearchApp({
  authenticated,
  userContext,
  open,
  onClose,
  navigate,
  smartSearchAccess,
  onContactUsClick = () => console.log('Contact Us clicked'),
  onProductClick = () => console.log('Product clicked'),
  onSolutionsClick = () => console.log('Solutions clicked')}) {
  const mfeProps = useMemo(() => ({ authenticated, userContext, navigate, smartSearchAccess }), [authenticated, userContext, navigate, smartSearchAccess]);

    const options = {
      targetElementId: SMART_SEARCH_ROOT_ID,
      authenticateUser: authenticated || false,
      postLoginRedirectUriOverride: null,
      navigate: navigate || window.singleSpa?.navigateToUrl,
      preFillFormOptions: [],
      userInfo: userContext || null,
      referringSource: 'APP_STORE_SMART_SEARCH'
  }

  useEffect(() => {
    let isMounted = true;
    async function mountSmartSearch() {
      if (!open || smartSearchAccess) return;
        try{
          await bootstrap(options);
          if (!isMounted) return;
          await mount(options, mfeProps, onContactUsClick, onProductClick, onSolutionsClick );
        }
        catch(error){
          console.error('Error mounting Smart Search:', error);
        }
      }
      mountSmartSearch();
    return () => {
      if (isMounted) {
        unmount(mfeProps);
        isMounted = false;
      }
    }
  }, [open, mfeProps, smartSearchAccess]);

  return (
    <SdfFocusPane 
      id={SMART_SEARCH_ROOT_ID}
      visible={open}
      onSdfDismiss={() => {
        sendGA4Data(HeaderSmartSearchClose);
        onClose();
      }}
      size="full"
      closeable
      dismissLabel="Exit"
      hideAcceptButton
    >
      {smartSearchAccess && (
          <PromoBoxContainer>
            <SdfPromoBox align="center"
              spot="illustration"
              illustrationName="browser-locked"
              header="Smart search unavailable"
              message="The smart search tool is being piloted only with ADP clients. We will let you know when it becomes available to ADP associates."
              layout_lg="vertical"
              variant="clear"
            />
          </PromoBoxContainer>
      )}
    </SdfFocusPane>
  );
}
